import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';

import AddMaintenanceDialog from '../../maintenances/AddMaintenanceDialog';
import AIWorkOrderDialog from '../../maintenances/ai/AIWorkOrderDialog';
import QuoteMachineDialog from '../../quote-machine/QuoteMachineDialog';
import CustomerDialog from '../../customers/CustomerDialog';
import CreateStandaloneInvoiceDialog from '../../invoices/CreateStandaloneInvoiceDialog';
import CommandPalette from './CommandPalette';
import useCommandPalette from './useCommandPalette';

const noop = () => {};
const CommandPaletteContext = createContext({ openPalette: noop, closePalette: noop, isOpen: false });

/** Open/close controls for the nav triggers. */
export function useCommandPaletteControls() {
  return useContext(CommandPaletteContext);
}

/**
 * Mounts the palette once in the authenticated shell, together with the "new X" dialogs its
 * actions open. These are the same dialog components the list pages mount, wired the way
 * widget-dashboard/DashboardDialogs.js wires them, so an action is the same entry point as the
 * page button.
 */
export default function CommandPaletteHost({ children }) {
  const navigate = useNavigate();
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const { open, openPalette, closePalette, initialQuery } = useCommandPalette();
  const [dialog, setDialog] = useState(null); // 'work_order' | 'ai' | 'quote_machine' | 'customer' | 'invoice'
  const [aiPreFill, setAiPreFill] = useState(null);
  const aiEnabled = Boolean(currentOrganization?.ai_work_order_enabled);
  const quoteMachineEnabled = Boolean(currentOrganization?.quote_machine_enabled);

  const closeDialog = useCallback(() => setDialog(null), []);

  const handleSwitchToAi = useCallback((data) => {
    setAiPreFill(data);
    setDialog('ai');
  }, []);

  const handleNavigate = useCallback(
    (url) => {
      closePalette();
      navigate(url);
    },
    [closePalette, navigate]
  );

  const handleRunAction = useCallback(
    (action) => {
      closePalette();
      if (action.path) {
        navigate(action.path);
      } else if (action.dialog) {
        setDialog(action.dialog);
      }
    },
    [closePalette, navigate]
  );

  const value = useMemo(() => ({ openPalette, closePalette, isOpen: open }), [openPalette, closePalette, open]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette open={open} onClose={closePalette} onNavigate={handleNavigate} onRunAction={handleRunAction} initialQuery={initialQuery} />
      <AddMaintenanceDialog
        open={dialog === 'work_order'}
        handleClose={closeDialog}
        onSwitchToAi={aiEnabled ? handleSwitchToAi : undefined}
      />
      {aiEnabled ? (
        <AIWorkOrderDialog
          open={dialog === 'ai'}
          handleClose={() => {
            closeDialog();
            setAiPreFill(null);
          }}
          vehicle={aiPreFill?.vehicle}
          customer={aiPreFill?.customer}
          initialText={aiPreFill?.text}
        />
      ) : null}
      {quoteMachineEnabled ? (
        <QuoteMachineDialog open={dialog === 'quote_machine'} handleClose={closeDialog} />
      ) : null}
      <CustomerDialog
        open={dialog === 'customer'}
        handleClose={closeDialog}
        addCreatedCustomer={(customer) => {
          closeDialog();
          navigate(`/customers?customer=${customer.id}`);
        }}
      />
      <CreateStandaloneInvoiceDialog
        open={dialog === 'invoice'}
        onClose={closeDialog}
        onInvoiceCreated={(invoice) => {
          closeDialog();
          navigate(`/invoices/${invoice.id}`);
        }}
      />
    </CommandPaletteContext.Provider>
  );
}
